import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button } from './button';
import { Dialog } from './dialog';

export interface ConfirmDialogProps {
  title?: string;
  description?: string;
  subject?: string;
  consequence?: 'remove' | 'stop';
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  disabled?: boolean;
  wide?: boolean;
  icon?: LucideIcon;
  children?: ReactNode;
  onConfirm: () => unknown;
  onClose: () => void;
}

export function ConfirmDialog({
  title,
  description,
  subject,
  consequence = 'remove',
  confirmLabel,
  cancelLabel,
  destructive,
  loading,
  disabled,
  wide,
  icon,
  children,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const common = useTranslation('common');
  const [pending, setPending] = useState(false);
  const busy = Boolean(loading) || pending;
  const consequenceText =
    consequence === 'stop'
      ? subject
        ? common.t('confirm.stop_named', { name: subject })
        : common.t('confirm.stop')
      : subject
        ? common.t('confirm.remove_named', { name: subject })
        : common.t('confirm.cannot_undo');
  const defaultConfirmLabel = consequence === 'stop' ? common.t('action.stop_using') : common.t(destructive ? 'action.remove' : 'action.confirm');

  const close = () => {
    if (!busy) onClose();
  };

  const confirm = async () => {
    if (busy || disabled) return;
    const result = onConfirm();
    if (!(result instanceof Promise)) return;
    setPending(true);
    try {
      await result;
    } catch {
      setPending(false);
      return;
    }
    setPending(false);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={close}
      title={title ?? common.t(consequence === 'stop' ? 'confirm.stop_title' : 'confirm.remove_title')}
      description={description}
      wide={wide}
      icon={icon}
      footer={
        <>
          <Button type="button" variant="outline" onClick={close} disabled={busy}>
            {cancelLabel ?? common.t('action.cancel')}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            loading={busy}
            disabled={disabled}
            onClick={() => void confirm()}
          >
            {confirmLabel ?? defaultConfirmLabel}
          </Button>
        </>
      }
    >
      {children ?? (
        <p className="text-sm text-muted-foreground">{consequenceText}</p>
      )}
    </Dialog>
  );
}
